import { Controller } from "@hotwired/stimulus"

// This controller manages the main Job openings filter panel
// It collects all job-related filters (currently_hiring, job_titles, job_posted_date, open_jobs_count, include_closed_roles)
// and dispatches them together when Apply is clicked
export default class extends Controller {
  static targets = ["currentlyHiring", "jobTitlesSummary", "departmentsSummary", "dateSummary", "countSummary", "includeClosedRoles", "divider", "filtersSection"]

  static values = {
    context: { type: String, default: "" }
  }

  connect() {
    // Store job titles values when tags-input applies them
    this.jobTitlesIncluded = []
    this.jobTitlesExcluded = []
    // Store job departments values when checkboxes apply them
    this.jobDepartmentsValues = []
  }

  // Toggle visibility of job filters based on "currently hiring" checkbox
  toggleJobFilters() {
    const isChecked = this.hasCurrentlyHiringTarget && this.currentlyHiringTarget.checked

    if (this.hasFiltersSectionTarget) {
      this.filtersSectionTarget.classList.toggle("muted", !isChecked)
    }
  }

  // Get the appropriate ID prefix based on context
  get idPrefix() {
    return this.contextValue || "leads"
  }

  apply(event) {
    event.preventDefault()

    // Collect all job-related filter values
    const filters = []

    // 1. Currently hiring checkbox
    const currentlyHiringValue = this.hasCurrentlyHiringTarget && this.currentlyHiringTarget.checked ? "true" : ""
    if (currentlyHiringValue) {
      filters.push({ name: "currently_hiring", values: currentlyHiringValue })
    }

    // 2. Job titles (from tags-input in drill-down)
    if (this.jobTitlesIncluded && this.jobTitlesIncluded.length > 0) {
      filters.push({ name: "job_titles_include", values: this.jobTitlesIncluded.join("|") })
    }
    if (this.jobTitlesExcluded && this.jobTitlesExcluded.length > 0) {
      filters.push({ name: "job_titles_exclude", values: this.jobTitlesExcluded.join("|") })
    }

    // 2.5 Job departments (from checkboxes in drill-down)
    if (this.jobDepartmentsValues && this.jobDepartmentsValues.length > 0) {
      filters.push({ name: "job_departments", values: this.jobDepartmentsValues.join("|") })
    }

    // 3. Job posted date (from simplified date filter in drill-down)
    const jobPostedDateRadio = this.element.querySelector(`#${this.idPrefix}-filter-job_posted_date-detail input[data-filters--date-filter-target="radioButton"]:checked`)
    if (jobPostedDateRadio && jobPostedDateRadio.value) {
      filters.push({ name: "job_posted_date", values: jobPostedDateRadio.value })
    }

    // 4. Number of open jobs (from simplified number range filter in drill-down)
    const openJobsFromInput = this.element.querySelector(`#${this.idPrefix}-filter-open_jobs_count-detail input[data-filters--number-range-filter-target="fromInput"]`)
    const openJobsToInput = this.element.querySelector(`#${this.idPrefix}-filter-open_jobs_count-detail input[data-filters--number-range-filter-target="toInput"]`)
    const fromValue = openJobsFromInput?.value || ""
    const toValue = openJobsToInput?.value || ""
    if (fromValue) {
      filters.push({ name: "open_jobs_count_from", values: fromValue })
    }
    if (toValue) {
      filters.push({ name: "open_jobs_count_to", values: toValue })
    }

    // 5. Include closed roles toggle
    const includeClosedRolesValue = this.hasIncludeClosedRolesTarget && this.includeClosedRolesTarget.checked ? "true" : ""
    if (includeClosedRolesValue) {
      filters.push({ name: "include_closed_roles", values: includeClosedRolesValue })
    }

    // Dispatch combined filter event with all job-related filters
    if (filters.length > 0) {
      filters.forEach(filter => {
        this.dispatch("filter", {
          detail: {
            name: filter.name,
            values: filter.values,
            context: this.contextValue
          }
        })
      })
    }
  }

  // Update job titles summary when returning from drill-down
  updateJobTitlesSummary() {
    if (!this.hasJobTitlesSummaryTarget) return

    // Count tag keywords (included + excluded)
    const includedCount = this.jobTitlesIncluded ? this.jobTitlesIncluded.length : 0
    const excludedCount = this.jobTitlesExcluded ? this.jobTitlesExcluded.length : 0
    const count = includedCount + excludedCount

    if (count === 0) {
      this.jobTitlesSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.all")
    } else {
      this.jobTitlesSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.keyword", { count })
    }
  }

  // Update job departments summary when returning from drill-down
  updateDepartmentsSummary() {
    if (!this.hasDepartmentsSummaryTarget) return

    const count = this.jobDepartmentsValues ? this.jobDepartmentsValues.length : 0

    if (count === 0) {
      this.departmentsSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.all")
    } else {
      this.departmentsSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.selected", { count })
    }
  }

  // Update date published summary when returning from drill-down
  updateDateSummary() {
    if (!this.hasDateSummaryTarget) return

    const jobPostedDateRadio = this.element.querySelector(`#${this.idPrefix}-filter-job_posted_date-detail input[data-filters--date-filter-target="radioButton"]:checked`)

    if (!jobPostedDateRadio || !jobPostedDateRadio.value) {
      this.dateSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.any_time")
      return
    }

    const value = jobPostedDateRadio.value
    if (value === "last7d") {
      this.dateSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.last_7_days")
    } else if (value === "last30d") {
      this.dateSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.last_30_days")
    } else if (value === "last90d") {
      this.dateSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.last_90_days")
    } else {
      this.dateSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.any_time")
    }
  }

  // Update number of roles summary when returning from drill-down
  updateCountSummary() {
    if (!this.hasCountSummaryTarget) return

    const openJobsFromInput = this.element.querySelector(`#${this.idPrefix}-filter-open_jobs_count-detail input[data-filters--number-range-filter-target="fromInput"]`)
    const openJobsToInput = this.element.querySelector(`#${this.idPrefix}-filter-open_jobs_count-detail input[data-filters--number-range-filter-target="toInput"]`)
    const fromValue = openJobsFromInput?.value || ""
    const toValue = openJobsToInput?.value || ""

    // Check if values are at defaults (from: 1 or empty, to: empty)
    const isDefaultFrom = !fromValue || fromValue === "1"
    const isDefaultTo = !toValue

    if (isDefaultFrom && isDefaultTo) {
      // Default state: show "At least 1"
      this.countSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.at_least_one")
    } else {
      // Custom values: show range
      this.countSummaryTarget.textContent = i18n.t("js.filters.job_openings_values.count_range", {
        from: fromValue || "1",
        to: toValue || "500"
      })
    }
  }

  // Update all summaries (called when returning to main view)
  updateAllSummaries() {
    this.updateJobTitlesSummary()
    this.updateDepartmentsSummary()
    this.updateDateSummary()
    this.updateCountSummary()
  }

  // Handle tags-input apply event
  handleTagsApply(event) {
    const { included, excluded } = event.detail
    // Split by pipe to get individual keywords
    this.jobTitlesIncluded = included && included.trim() ? included.split("|").filter(v => v.trim()) : []
    this.jobTitlesExcluded = excluded && excluded.trim() ? excluded.split("|").filter(v => v.trim()) : []
    this.updateJobTitlesSummary()
    this.hidePanel(`#${this.idPrefix}-filter-job_titles-detail`)
    this.showPanel(`#${this.idPrefix}-filter-job_openings-header`)
  }

  // Apply job titles changes and return to main view (for backward compatibility)
  applyJobTitles(event) {
    event.preventDefault()
    // This is now handled by handleTagsApply
  }

  // Apply departments changes and return to main view
  applyDepartments(event) {
    event.preventDefault()

    // Get all checked department checkboxes
    const departmentCheckboxes = this.element.querySelectorAll(`#${this.idPrefix}-filter-job_departments-detail input[data-filters--checkbox-filter-target="option"]:checked`)
    this.jobDepartmentsValues = Array.from(departmentCheckboxes).map(cb => cb.value)

    this.updateDepartmentsSummary()
    this.hidePanel(`#${this.idPrefix}-filter-job_departments-detail`)
    this.showPanel(`#${this.idPrefix}-filter-job_openings-header`)
  }

  // Apply date changes and return to main view
  applyDate(event) {
    event.preventDefault()
    this.updateDateSummary()
    this.hidePanel(`#${this.idPrefix}-filter-job_posted_date-detail`)
    this.showPanel(`#${this.idPrefix}-filter-job_openings-header`)
  }

  // Apply count changes and return to main view
  applyCount(event) {
    event.preventDefault()
    this.updateCountSummary()
    const detailSelector = `#${this.idPrefix}-filter-open_jobs_count-detail`
    const headerSelector = `#${this.idPrefix}-filter-job_openings-header`
    this.hidePanel(detailSelector)
    this.showPanel(headerSelector)
  }

  // Helper to show a panel
  showPanel(selector) {
    const panel = this.element.querySelector(selector)
    if (panel) {
      panel.classList.remove("d-none")
    }
  }

  // Helper to hide a panel
  hidePanel(selector) {
    const panel = this.element.querySelector(selector)
    if (panel) {
      panel.classList.add("d-none")
    }
  }
}
